import React, { useState, useEffect } from 'react';
import { apiClient } from '../../api';

interface LLMModel {
  name: string;
  value: string;
  cost_per_1k_input: number;
  cost_per_1k_output: number;
}

interface LLMProvider {
  name: string;
  enum_value: string;
  models: LLMModel[];
}

interface GatewayConfig {
  llm?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
    providers?: LLMProvider[];
  };
  mcp_proxies?: {
    paths?: Array<{ id: string; name: string; prefix: string }>;
  };
}

interface CostStepProps {
  provider: string;
  model: string;
  initialEnableCostTracking?: boolean;
  initialInputCostPer1K?: number;
  initialOutputCostPer1K?: number;
  initialMonthlyBudget?: number;
  onChange: (data: {
    enableCostTracking: boolean;
    inputCostPer1K: number;
    outputCostPer1K: number;
    monthlyBudget: number;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const CostStep: React.FC<CostStepProps> = ({
  provider,
  model,
  initialEnableCostTracking = true,
  initialInputCostPer1K = 0,
  initialOutputCostPer1K = 0,
  initialMonthlyBudget = 0,
  onChange,
  onNext,
  onBack,
  onCancel
}) => {
  const [enableCostTracking, setEnableCostTracking] = useState(initialEnableCostTracking);
  const [inputCostPer1K, setInputCostPer1K] = useState(initialInputCostPer1K);
  const [outputCostPer1K, setOutputCostPer1K] = useState(initialOutputCostPer1K);
  const [monthlyBudget, setMonthlyBudget] = useState(initialMonthlyBudget);
  const [enableBudgetLimit, setEnableBudgetLimit] = useState(initialMonthlyBudget > 0);
  const [providers, setProviders] = useState<LLMProvider[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    loadProviders();
  }, []);

  const loadProviders = async () => {
    try {
      const response = await apiClient.get('/pipes/config');
      const config: GatewayConfig = response.data;
      if (config.llm?.providers) {
        setProviders(config.llm.providers);
        
        // Automatically set default pricing from config
        const selectedProvider = config.llm.providers.find(p => p.enum_value === provider);
        if (selectedProvider) {
          const selectedModel = selectedProvider.models.find(m => m.value === model);
          if (selectedModel && !configLoaded) {
            setInputCostPer1K(selectedModel.cost_per_1k_input);
            setOutputCostPer1K(selectedModel.cost_per_1k_output);
            onChange({
              enableCostTracking,
              inputCostPer1K: selectedModel.cost_per_1k_input,
              outputCostPer1K: selectedModel.cost_per_1k_output,
              monthlyBudget
            });
            setConfigLoaded(true);
          }
        }
      }
    } catch (err) {
      console.error('Failed to load providers:', err);
    }
  };

  // Get pricing from gateway config
  const getDefaultPricing = (): { input: number; output: number } => {
    const selectedProvider = providers.find(p => p.enum_value === provider);
    if (selectedProvider) {
      const selectedModel = selectedProvider.models.find(m => m.value === model);
      if (selectedModel) {
        return {
          input: selectedModel.cost_per_1k_input,
          output: selectedModel.cost_per_1k_output
        };
      }
    }
    return { input: 0, output: 0 };
  };

  const handleUseDefaultPricing = () => {
    const defaults = getDefaultPricing();
    setInputCostPer1K(defaults.input);
    setOutputCostPer1K(defaults.output);
    onChange({
      enableCostTracking,
      inputCostPer1K: defaults.input,
      outputCostPer1K: defaults.output,
      monthlyBudget
    });
  };

  const handleInputCostChange = (value: string) => {
    const num = parseFloat(value) || 0;
    setInputCostPer1K(num);
    onChange({ enableCostTracking, inputCostPer1K: num, outputCostPer1K, monthlyBudget });
  };

  const handleOutputCostChange = (value: string) => {
    const num = parseFloat(value) || 0;
    setOutputCostPer1K(num);
    onChange({ enableCostTracking, inputCostPer1K, outputCostPer1K: num, monthlyBudget });
  };

  const handleBudgetChange = (value: string) => {
    const num = parseFloat(value) || 0;
    setMonthlyBudget(num);
    onChange({ enableCostTracking, inputCostPer1K, outputCostPer1K, monthlyBudget: num });
  };

  const handleToggleCostTracking = (enabled: boolean) => {
    setEnableCostTracking(enabled);
    onChange({
      enableCostTracking: enabled,
      inputCostPer1K: enabled ? inputCostPer1K : 0,
      outputCostPer1K: enabled ? outputCostPer1K : 0,
      monthlyBudget: enabled && enableBudgetLimit ? monthlyBudget : 0
    });
  };

  const handleToggleBudgetLimit = (enabled: boolean) => {
    setEnableBudgetLimit(enabled);
    onChange({
      enableCostTracking,
      inputCostPer1K,
      outputCostPer1K,
      monthlyBudget: enabled ? monthlyBudget : 0
    });
  };

  const handleNext = () => {
    onChange({
      enableCostTracking,
      inputCostPer1K: enableCostTracking ? inputCostPer1K : 0,
      outputCostPer1K: enableCostTracking ? outputCostPer1K : 0,
      monthlyBudget: enableCostTracking && enableBudgetLimit ? monthlyBudget : 0
    });
    onNext();
  };

  // Calculate estimated cost for example usage
  const calculateEstimatedCost = (inputTokens: number, outputTokens: number): string => {
    const inputCost = (inputTokens / 1000) * inputCostPer1K;
    const outputCost = (outputTokens / 1000) * outputCostPer1K;
    return (inputCost + outputCost).toFixed(4);
  };

  return (
    <div className="card shadow">
      <div className="card-body">
        <h4 className="mb-3">
          <i className="fas fa-dollar-sign mr-2"></i>
          Cost Tracking & Budgets
        </h4>

        <p className="text-muted mb-4">
          Track LLM usage costs and set budget limits to control spending.
        </p>

        <div className="form-group">
          <div className="custom-control custom-switch">
            <input
              type="checkbox"
              className="custom-control-input"
              id="enableCostTracking"
              checked={enableCostTracking}
              onChange={(e) => handleToggleCostTracking(e.target.checked)}
            />
            <label className="custom-control-label" htmlFor="enableCostTracking">
              Enable Cost Tracking
            </label>
          </div>
          <small className="form-text text-muted">
            Track token usage and calculate costs based on your pricing configuration.
          </small>
        </div>

        {enableCostTracking && (
          <>
            <div className="alert alert-info">
              <i className="fas fa-info-circle mr-2"></i>
              <strong>Cost Tracking Benefits:</strong>
              <ul className="mb-0 mt-2">
                <li>Monitor real-time LLM usage costs</li>
                <li>Set monthly budget limits with automatic alerts</li>
                <li>Track cost trends over time</li>
                <li>Prevent unexpected charges</li>
              </ul>
            </div>

            <div className="row">
              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="inputCostPer1K">
                    Input Cost (per 1K tokens)
                    <span className="text-danger ml-1">*</span>
                  </label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      id="inputCostPer1K"
                      value={inputCostPer1K}
                      onChange={(e) => handleInputCostChange(e.target.value)}
                      min="0"
                      step="0.0001"
                      required
                    />
                  </div>
                  <small className="form-text text-muted">
                    Cost per 1,000 input tokens
                  </small>
                </div>
              </div>

              <div className="col-md-6">
                <div className="form-group">
                  <label htmlFor="outputCostPer1K">
                    Output Cost (per 1K tokens)
                    <span className="text-danger ml-1">*</span>
                  </label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">$</span>
                    </div>
                    <input
                      type="number"
                      className="form-control"
                      id="outputCostPer1K"
                      value={outputCostPer1K}
                      onChange={(e) => handleOutputCostChange(e.target.value)}
                      min="0"
                      step="0.0001"
                      required
                    />
                  </div>
                  <small className="form-text text-muted">
                    Cost per 1,000 output tokens
                  </small>
                </div>
              </div>
            </div>

            {(inputCostPer1K > 0 || outputCostPer1K > 0) && (
              <div className="card bg-light mb-3">
                <div className="card-body">
                  <h6 className="font-weight-bold mb-3">
                    <i className="fas fa-calculator mr-2"></i>
                    Example Cost Calculation
                  </h6>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="text-muted small mb-1">Small Request</div>
                      <div className="font-weight-bold">
                        ${calculateEstimatedCost(500, 500)}
                      </div>
                      <div className="text-muted small">500 in / 500 out</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small mb-1">Medium Request</div>
                      <div className="font-weight-bold">
                        ${calculateEstimatedCost(2000, 1000)}
                      </div>
                      <div className="text-muted small">2K in / 1K out</div>
                    </div>
                    <div className="col-md-4">
                      <div className="text-muted small mb-1">Large Request</div>
                      <div className="font-weight-bold">
                        ${calculateEstimatedCost(8000, 2000)}
                      </div>
                      <div className="text-muted small">8K in / 2K out</div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <hr className="my-4" />

            <div className="form-group">
              <div className="custom-control custom-switch">
                <input
                  type="checkbox"
                  className="custom-control-input"
                  id="enableBudgetLimit"
                  checked={enableBudgetLimit}
                  onChange={(e) => handleToggleBudgetLimit(e.target.checked)}
                />
                <label className="custom-control-label" htmlFor="enableBudgetLimit">
                  Set Monthly Budget Limit
                </label>
              </div>
              <small className="form-text text-muted">
                Optionally set a monthly spending limit to prevent cost overruns.
              </small>
            </div>

            {enableBudgetLimit && (
              <div className="form-group">
                <label htmlFor="monthlyBudget">
                  Monthly Budget Limit
                  <span className="text-danger ml-1">*</span>
                </label>
                <div className="input-group">
                  <div className="input-group-prepend">
                    <span className="input-group-text">$</span>
                  </div>
                  <input
                    type="number"
                    className="form-control"
                    id="monthlyBudget"
                    value={monthlyBudget}
                    onChange={(e) => handleBudgetChange(e.target.value)}
                    min="0"
                    step="10"
                    required
                  />
                </div>
                <small className="form-text text-muted">
                  Maximum amount to spend per month. An alert will be triggered when approaching this limit.
                </small>
              </div>
            )}
          </>
        )}

        {!enableCostTracking && (
          <div className="alert alert-warning">
            <i className="fas fa-exclamation-triangle mr-2"></i>
            <strong>No Cost Tracking:</strong> LLM usage will not be tracked for cost analysis.
            You will not receive budget alerts or cost reports.
          </div>
        )}

        <div className="d-flex justify-content-between mt-4">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onBack}
          >
            <i className="fas fa-arrow-left mr-1"></i>
            Back
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleNext}
          >
            Next
            <i className="fas fa-arrow-right ml-1"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CostStep;
